// src/components/MemoSection.tsx
import React, { useId } from "react";
import clsx from "clsx";
import { A } from "../styles/appleClasses";
import { IconButton } from "./IconButton";
import { PencilIcon } from "@heroicons/react/24/solid";

type MemoSectionProps = {
  label?: string;

  /** The memo text (controlled). */
  value: string;

  /** If provided, component is editable in mode="edit". */
  onChange?: (v: string) => void;

  /** "edit" = textarea, "view" = read-only */
  mode?: "edit" | "view";

  /** Only used in edit mode */
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  disabled?: boolean;

  /** Only used in view mode */
  emptyText?: string;

  /** Wrap style */
  variant?: "plain" | "card";

  /** Optional edit affordance in view mode */
  onEditClick?: () => void;
  editAriaLabel?: string;

  className?: string;
};

export function MemoSection({
  label = "Memo",
  value,
  onChange,
  mode = "edit",
  placeholder = "Add note (optional)",
  maxLength = 500,
  rows = 2,
  disabled = false,
  emptyText = "—",
  variant = "plain",
  onEditClick,
  editAriaLabel = "Edit memo",
  className = "",
}: MemoSectionProps) {
  const id = useId();
  const labelCls = clsx("block mb-1", A.label);

  const wrapCls = clsx(
    variant === "card" ? `${A.card} p-4 relative` : "",
    className
  );

  if (mode === "view") {
    const hasText = !!(value && value.trim());

    return (
      <div className={wrapCls}>
        <div className={labelCls}>{label}</div>

        {onEditClick ? (
          <IconButton
            aria-label={editAriaLabel}
            onClick={onEditClick}
            className="absolute top-3 right-3"
            disabled={disabled}
          >
            <PencilIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </IconButton>
        ) : null}

        <div
          className={clsx(
            A.body,
            "whitespace-pre-wrap",
            hasText ? "text-gray-700" : "text-gray-400"
          )}
        >
          {hasText ? value : emptyText}
        </div>
      </div>
    );
  }

  // mode === "edit"
  return (
    <div className={wrapCls}>
      <label className={labelCls} htmlFor={id}>
        {label}
      </label>

      <textarea
        id={id}
        className={clsx(A.inputBase, "h-auto py-2 resize-none")}
        rows={rows}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
      />
    </div>
  );
}

export default MemoSection;
